import React from "react";
import { useTranslation } from "react-i18next";

// Custom Components
import Table from "../../components/Table/Table";
import PositionAverages from "./PositionAverages";
import GenericAvatar from "../../components/Icons/GenericAvatar";
import PositionEmployeesTable from "./PositionEmployeesTable";
import PositionRoles from "./PositionRoles";
import { useRoles } from "../../hooks/roles";
import { statusTypes } from "../../constants/index";
import AdministrationInfoLayout from "../../components/Layouts/AdministrationInfoLayout";
import { Typography } from "@mui/material";

const groupColumns = [
  {
    title: "Name",
    field: "name"
  }
];

const PositionInformation = ({
  positionId,
  position,
  onUpdatePosition,
  isLoading: isLoadingPosition
}) => {
  const { t } = useTranslation();
  const { roles, isLoading: isLoadingRoles } = useRoles({
    statusType: statusTypes.active,
    assignableByPosition: true,
    positionId: positionId
  });

  const handleUpdateRoles = newRoles => {
    const newPositionData = {
      ...position,
      roles: newRoles
        .filter(role => !!Object.values(role)[0]) // Filter out unselected roles
        .map(role => ({ id: Object.keys(role)[0], type: "role" }))
    };
    return onUpdatePosition(newPositionData);
  };

  const groupsTable = (
    <Table
      itemType="record"
      columns={groupColumns}
      data={position ? position.groups : []}
      hidePagination={true}
      isLoading={isLoadingPosition}
    />
  );

  const employeesTable = <PositionEmployeesTable positionId={positionId} />;

  return (
    <AdministrationInfoLayout
      name={position ? position.name : ""}
      image={<GenericAvatar icon="position" />}
      subtitle1={
        <Typography
          variant="subtitle1"
          color="textSecondary"
          sx={{ pb: 0.75 }}
          data-qa="position-info-id-and-employee-count"
        >
          {t("administration.positionId")}:{" "}
          {position ? position.customerPositionId : ""} |{" "}
          {t("administration.employees")}:{" "}
          {position ? position.employeesCount : ""}
        </Typography>
      }
      compliance={position ? position.compliance : 0}
      completedTraining={position ? position.completedTraining : 0}
      trainingHours={position ? position.trainingHours : 0}
      averageScore={position ? position.averageScore : 0}
      averages={<PositionAverages positionId={positionId} />}
      roles={
        <PositionRoles
          allRoles={roles}
          positionRoles={position ? position.roles : []}
          onUpdateRoles={handleUpdateRoles}
        />
      }
      tables={[
        { header: t("administration.groupsHeader"), table: groupsTable },
        {
          header: t("administration.activeEmployeesHeader"),
          table: employeesTable
        }
      ]}
      isLoadingInfo={isLoadingPosition}
      isLoadingRoles={isLoadingRoles}
    />
  );
};

export default PositionInformation;
